/**
 * AssetPhotoGallery Component
 *
 * A component for managing asset photos with upload, display, delete, and set-primary functionality.
 * Photos are displayed as thumbnails in a grid with hover actions.
 *
 * @example
 * ```jsx
 * <AssetPhotoGallery
 *   assetId="asset-uuid"
 *   readOnly={false}
 * />
 * ```
 */

import { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import Compressor from 'compressorjs';
import {
	XMarkIcon,
	PhotoIcon,
	PlusIcon,
	StarIcon,
	ArrowDownTrayIcon,
	TrashIcon,
} from '@heroicons/react/20/solid';
import useAsync from '../../hooks/useAsync';
import {
	getAssetPhotos,
	uploadAssetPhotos,
	deleteAssetPhoto,
	setAssetPrimaryPhoto,
} from '../../api/assets';
import { DangerModal } from '../Shell/Modal';

const classNames = (...classes) => classes.filter(Boolean).join(' ');

/**
 * Context menu for photo actions (mobile long-press)
 */
const PhotoContextMenu = ({ position, onClose, onDownload, onDelete }) => {
	const menuRef = useRef(null);

	useEffect(() => {
		const handleClickOutside = (e) => {
			if (menuRef.current && !menuRef.current.contains(e.target)) {
				onClose();
			}
		};

		const handleScroll = () => onClose();

		document.addEventListener('mousedown', handleClickOutside);
		document.addEventListener('touchstart', handleClickOutside);
		document.addEventListener('scroll', handleScroll, true);

		return () => {
			document.removeEventListener('mousedown', handleClickOutside);
			document.removeEventListener('touchstart', handleClickOutside);
			document.removeEventListener('scroll', handleScroll, true);
		};
	}, [onClose]);

	// Adjust position to keep menu in viewport
	const adjustedPosition = {
		top: Math.min(position.top, window.innerHeight - 120),
		left: Math.min(Math.max(position.left - 80, 10), window.innerWidth - 170),
	};

	return createPortal(
		<div
			ref={menuRef}
			className="fixed z-50 w-40 rounded-lg bg-white shadow-lg ring-1 ring-black ring-opacity-5 overflow-hidden select-none"
			style={{ top: adjustedPosition.top, left: adjustedPosition.left }}
		>
			<button
				type="button"
				className="w-full px-4 py-3 text-left text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-3"
				onClick={() => {
					onDownload();
					onClose();
				}}
			>
				<ArrowDownTrayIcon className="h-5 w-5 text-gray-500" />
				Download
			</button>
			<button
				type="button"
				className="w-full px-4 py-3 text-left text-sm text-red-600 hover:bg-red-50 flex items-center gap-3 border-t border-gray-100"
				onClick={() => {
					onDelete();
					onClose();
				}}
			>
				<TrashIcon className="h-5 w-5" />
				Delete
			</button>
		</div>,
		document.body
	);
};

/**
 * Single photo thumbnail component
 */
const PhotoThumbnail = ({
	photo,
	onDelete,
	onSetPrimary,
	readOnly = false,
}) => {
	const thumbnailUrl = photo?.thumbnailLocation || photo?.location || '';
	const fullUrl = photo?.location || '';
	const name = photo?.name || photo?.originalName || 'Photo';
	const isPrimary = photo?.isPrimary || false;

	// Long-press state for mobile
	const [contextMenu, setContextMenu] = useState(null);
	const longPressTimer = useRef(null);
	const touchStartPos = useRef(null);

	const handleDelete = (e) => {
		e?.stopPropagation?.();
		if (onDelete) {
			onDelete(photo);
		}
	};

	const handleSetPrimary = (e) => {
		e.stopPropagation();
		if (onSetPrimary && !isPrimary) {
			onSetPrimary(photo);
		}
	};

	const handleClick = () => {
		// Don't open if context menu is showing
		if (contextMenu) return;
		// Open full-size image in new tab
		if (fullUrl) {
			window.open(fullUrl, '_blank');
		}
	};

	const handleDownload = useCallback(() => {
		if (fullUrl) {
			// Create a temporary link to trigger download
			const link = document.createElement('a');
			link.href = fullUrl;
			link.download = name;
			link.target = '_blank';
			document.body.appendChild(link);
			link.click();
			document.body.removeChild(link);
		}
	}, [fullUrl, name]);

	// Long-press handlers for mobile
	const handleTouchStart = (e) => {
		if (readOnly) return;

		const touch = e.touches[0];
		touchStartPos.current = { x: touch.clientX, y: touch.clientY };

		longPressTimer.current = setTimeout(() => {
			// Vibrate if supported (haptic feedback)
			if (navigator.vibrate) {
				navigator.vibrate(50);
			}

			setContextMenu({
				top: touch.clientY,
				left: touch.clientX,
			});
		}, 500); // 500ms long-press threshold
	};

	const handleTouchMove = (e) => {
		// Cancel long-press if user moves finger too much
		if (longPressTimer.current && touchStartPos.current) {
			const touch = e.touches[0];
			const deltaX = Math.abs(touch.clientX - touchStartPos.current.x);
			const deltaY = Math.abs(touch.clientY - touchStartPos.current.y);

			if (deltaX > 10 || deltaY > 10) {
				clearTimeout(longPressTimer.current);
				longPressTimer.current = null;
			}
		}
	};

	const handleTouchEnd = () => {
		if (longPressTimer.current) {
			clearTimeout(longPressTimer.current);
			longPressTimer.current = null;
		}
	};

	// Prevent default context menu on long-press
	const handleContextMenu = (e) => {
		if (!readOnly) {
			e.preventDefault();
		}
	};

	return (
		<>
			<div
				className={classNames(
					'relative group rounded-lg overflow-hidden cursor-pointer',
					'border-2 transition-all hover:shadow-md',
					isPrimary
						? 'border-blue-500 ring-2 ring-blue-200'
						: 'border-gray-200 hover:border-gray-300'
				)}
				onClick={handleClick}
				onTouchStart={handleTouchStart}
				onTouchMove={handleTouchMove}
				onTouchEnd={handleTouchEnd}
				onTouchCancel={handleTouchEnd}
				onContextMenu={handleContextMenu}
				title={name}
				style={{ width: '96px', height: '96px' }}
			>
				{thumbnailUrl ? (
					<img
						src={thumbnailUrl}
						alt={name}
						className="w-full h-full object-cover"
						draggable={false}
					/>
				) : (
					<div className="w-full h-full flex items-center justify-center bg-gray-100">
						<PhotoIcon className="w-8 h-8 text-gray-400" />
					</div>
				)}

				{/* Primary badge */}
				{isPrimary && (
					<div className="absolute top-1 left-1 bg-blue-500 text-white px-1.5 py-0.5 rounded text-xs font-medium flex items-center gap-0.5">
						<StarIcon className="h-3 w-3" />
						Primary
					</div>
				)}

				{/* Hover overlay */}
				<div
					className={classNames(
						'absolute inset-0 bg-black/0 group-hover:bg-black/30',
						'transition-all'
					)}
				/>

				{/* Action buttons on hover (desktop) */}
				{!readOnly && (
					<div className="absolute top-1 right-1 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
						{/* Set Primary button */}
						{!isPrimary && onSetPrimary && (
							<button
								type="button"
								className={classNames(
									'p-1 rounded-full',
									'bg-white/90 hover:bg-blue-500',
									'text-gray-600 hover:text-white',
									'shadow-sm transition-colors'
								)}
								onClick={handleSetPrimary}
								title="Set as primary"
							>
								<StarIcon className="h-3.5 w-3.5" />
							</button>
						)}

						{/* Delete button */}
						{onDelete && (
							<button
								type="button"
								className={classNames(
									'p-1 rounded-full',
									'bg-white/90 hover:bg-red-500',
									'text-gray-600 hover:text-white',
									'shadow-sm transition-colors'
								)}
								onClick={handleDelete}
								title="Delete photo"
							>
								<XMarkIcon className="h-3.5 w-3.5" />
							</button>
						)}
					</div>
				)}
			</div>

			{/* Context menu (mobile long-press) */}
			{contextMenu && (
				<PhotoContextMenu
					position={contextMenu}
					onClose={() => setContextMenu(null)}
					onDownload={handleDownload}
					onDelete={handleDelete}
				/>
			)}
		</>
	);
};

/**
 * Upload button component
 */
const UploadButton = ({ onUpload, isUploading, disabled }) => {
	const fileInputRef = useRef(null);

	const handleClick = () => {
		fileInputRef.current?.click();
	};

	const handleFileChange = async (e) => {
		const selectedFiles = Array.from(e.target.files || []);
		if (selectedFiles.length === 0) return;

		const compressedFiles = await compressFiles(selectedFiles);
		onUpload(compressedFiles);

		// Reset input
		e.target.value = '';
	};

	return (
		<>
			<input
				ref={fileInputRef}
				type="file"
				accept="image/jpeg,image/png,image/webp,image/heic"
				multiple
				className="hidden"
				onChange={handleFileChange}
				disabled={disabled || isUploading}
			/>
			<button
				type="button"
				onClick={handleClick}
				disabled={disabled || isUploading}
				className={classNames(
					'flex items-center justify-center rounded-lg',
					'border-2 border-dashed border-gray-300 hover:border-blue-400',
					'bg-gray-50 hover:bg-blue-50',
					'text-gray-500 hover:text-blue-600',
					'transition-all cursor-pointer',
					(disabled || isUploading) && 'opacity-50 cursor-not-allowed'
				)}
				style={{ width: '96px', height: '96px' }}
				title="Upload photos"
			>
				{isUploading ? (
					<div className="animate-spin h-6 w-6 border-2 border-blue-500 border-t-transparent rounded-full" />
				) : (
					<div className="flex flex-col items-center gap-1">
						<PlusIcon className="h-6 w-6" />
						<span className="text-xs">Add</span>
					</div>
				)}
			</button>
		</>
	);
};

/**
 * Compress image files before upload
 */
const compressFiles = async (files) => {
	return Promise.all(
		files.map(
			(file) =>
				new Promise((resolve) => {
					if (!file.type.startsWith('image/')) {
						resolve(file);
						return;
					}

					new Compressor(file, {
						quality: 0.8,
						maxWidth: 1920,
						maxHeight: 1920,
						success: (compressedFile) => {
							// Preserve original filename
							const newFile = new File([compressedFile], file.name, {
								type: compressedFile.type,
							});
							resolve(newFile);
						},
						error: () => {
							resolve(file);
						},
					});
				})
		)
	);
};

/**
 * Main AssetPhotoGallery component
 */
const AssetPhotoGallery = ({
	assetId,
	readOnly = false,
	className = '',
	showLabel = true,
	onPhotosChange,
}) => {
	const [isUploading, setIsUploading] = useState(false);
	const [deleteModalOpen, setDeleteModalOpen] = useState(false);
	const [photoToDelete, setPhotoToDelete] = useState(null);
	const [isDragging, setIsDragging] = useState(false);
	const dragCounter = useRef(0);

	// Fetch photos
	const {
		value: photosData,
		execute: refreshPhotos,
		status: loadStatus,
	} = useAsync(getAssetPhotos, {
		immediate: !!assetId,
		defaultPayload: { assetId },
	});

	// Upload photos
	const { execute: doUpload } = useAsync(uploadAssetPhotos, {
		immediate: false,
		showNotification: { success: 'Photos uploaded successfully' },
	});

	// Delete photo
	const { execute: doDelete } = useAsync(deleteAssetPhoto, {
		immediate: false,
		showNotification: { success: 'Photo deleted' },
	});

	// Set primary photo
	const { execute: doSetPrimary } = useAsync(setAssetPrimaryPhoto, {
		immediate: false,
		showNotification: { success: 'Primary photo updated' },
	});

	const photos = photosData?.data?.photos || [];
	const isLoading = loadStatus === 'pending';

	// Handle upload
	const handleUpload = async (files) => {
		if (!files.length || !assetId) return;

		setIsUploading(true);
		try {
			await doUpload({ assetId, photos: files });
			await refreshPhotos({ assetId });
			onPhotosChange?.();
		} finally {
			setIsUploading(false);
		}
	};

	// Open delete confirmation modal
	const handleDeleteClick = (photo) => {
		setPhotoToDelete(photo);
		setDeleteModalOpen(true);
	};

	// Handle confirmed delete
	const handleConfirmDelete = async () => {
		if (!photoToDelete) return;

		try {
			await doDelete({ assetId, photoId: photoToDelete.id });
			await refreshPhotos({ assetId });
			onPhotosChange?.();
		} catch (error) {
			console.error('Failed to delete photo:', error);
		} finally {
			setDeleteModalOpen(false);
			setPhotoToDelete(null);
		}
	};

	// Close delete modal
	const handleCancelDelete = () => {
		setDeleteModalOpen(false);
		setPhotoToDelete(null);
	};

	// Handle set primary
	const handleSetPrimary = async (photo) => {
		try {
			await doSetPrimary({ assetId, photoId: photo.id });
			await refreshPhotos({ assetId });
			onPhotosChange?.();
		} catch (error) {
			console.error('Failed to set primary photo:', error);
		}
	};

	// Drag and drop handlers
	const handleDragEnter = (e) => {
		e.preventDefault();
		e.stopPropagation();
		dragCounter.current++;
		if (e.dataTransfer.items && e.dataTransfer.items.length > 0) {
			setIsDragging(true);
		}
	};

	const handleDragLeave = (e) => {
		e.preventDefault();
		e.stopPropagation();
		dragCounter.current--;
		if (dragCounter.current === 0) {
			setIsDragging(false);
		}
	};

	const handleDragOver = (e) => {
		e.preventDefault();
		e.stopPropagation();
	};

	const handleDrop = async (e) => {
		e.preventDefault();
		e.stopPropagation();
		setIsDragging(false);
		dragCounter.current = 0;

		if (readOnly || isUploading) return;

		const droppedFiles = Array.from(e.dataTransfer.files || []);
		// Filter to only image files
		const imageFiles = droppedFiles.filter((file) =>
			file.type.startsWith('image/')
		);

		if (imageFiles.length === 0) return;

		const compressedFiles = await compressFiles(imageFiles);
		handleUpload(compressedFiles);
	};

	// Don't render if no assetId
	if (!assetId) return null;

	return (
		<div
			className={classNames(
				className,
				!readOnly && 'relative'
			)}
			onDragEnter={!readOnly ? handleDragEnter : undefined}
			onDragLeave={!readOnly ? handleDragLeave : undefined}
			onDragOver={!readOnly ? handleDragOver : undefined}
			onDrop={!readOnly ? handleDrop : undefined}
		>
			{showLabel && (
				<div className="text-xs text-gray-500 font-medium uppercase tracking-wider mb-3">
					Photos
				</div>
			)}

			{/* Drag overlay */}
			{isDragging && !readOnly && (
				<div className="absolute inset-0 bg-blue-50 border-2 border-dashed border-blue-400 rounded-lg z-10 flex items-center justify-center">
					<div className="text-blue-600 font-medium flex items-center gap-2">
						<PhotoIcon className="h-6 w-6" />
						Drop photos here
					</div>
				</div>
			)}

			{isLoading ? (
				<div className="flex items-center gap-2 text-sm text-gray-500">
					<div className="animate-spin h-4 w-4 border-2 border-blue-500 border-t-transparent rounded-full" />
					Loading photos...
				</div>
			) : (
				<div className="flex flex-wrap gap-3">
					{/* Photo thumbnails */}
					{photos.map((photo) => (
						<PhotoThumbnail
							key={photo.id}
							photo={photo}
							onDelete={readOnly ? null : handleDeleteClick}
							onSetPrimary={readOnly ? null : handleSetPrimary}
							readOnly={readOnly}
						/>
					))}

					{/* Upload button */}
					{!readOnly && (
						<UploadButton
							onUpload={handleUpload}
							isUploading={isUploading}
						/>
					)}

					{/* Empty state */}
					{photos.length === 0 && readOnly && (
						<p className="text-sm text-gray-500">No photos</p>
					)}
				</div>
			)}

			{/* Delete confirmation modal */}
			<DangerModal
				open={deleteModalOpen}
				onClose={handleCancelDelete}
				title="Delete Photo"
				confirmText="Delete"
				onConfirm={handleConfirmDelete}
				cancelText="Cancel"
			>
				Are you sure you want to delete this photo? This action cannot be undone.
			</DangerModal>
		</div>
	);
};

export default AssetPhotoGallery;
